import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceChecklist = ({ className = '' }) => {
  const [checklistData, setChecklistData] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedSections, setExpandedSections] = useState(new Set());

  useEffect(() => {
    const mockChecklistData = [
      {
        id: 'gmp-001',
        framework: 'GMP',
        category: 'Documentation',
        title: 'Standard Operating Procedures (SOPs)',
        description: 'All manufacturing processes must have current, approved SOPs',
        requirements: [
          { id: 'sop-001', text: 'Manufacturing SOPs updated and approved', status: 'completed', deadline: new Date('2024-08-15') },
          { id: 'sop-002', text: 'Quality Control SOPs reviewed', status: 'completed', deadline: new Date('2024-08-20') },
          { id: 'sop-003', text: 'Cleaning validation SOPs documented', status: 'in_progress', deadline: new Date('2024-09-30') },
          { id: 'sop-004', text: 'Equipment maintenance SOPs verified', status: 'pending', deadline: new Date('2024-10-15') }
        ],
        overallStatus: 'in_progress',
        completion: 75,
        priority: 'high'
      },
      {
        id: 'ema-001',
        framework: 'EMA',
        category: 'Quality Management',
        title: 'Pharmaceutical Quality System',
        description: 'Implementation of comprehensive quality management system',
        requirements: [
          { id: 'pqs-001', text: 'Quality policy documented and communicated', status: 'completed', deadline: new Date('2024-07-01') },
          { id: 'pqs-002', text: 'Quality objectives defined and measurable', status: 'completed', deadline: new Date('2024-07-15') },
          { id: 'pqs-003', text: 'Management review process established', status: 'in_progress', deadline: new Date('2024-09-25') },
          { id: 'pqs-004', text: 'Corrective and preventive action system', status: 'completed', deadline: new Date('2024-08-10') }
        ],
        overallStatus: 'in_progress',
        completion: 85,
        priority: 'medium'
      },
      {
        id: 'iso-001',
        framework: 'ISO 17025',
        category: 'Laboratory Management',
        title: 'Testing Laboratory Competence',
        description: 'General requirements for competence of testing laboratories',
        requirements: [
          { id: 'lab-001', text: 'Laboratory accreditation maintained', status: 'completed', deadline: new Date('2024-06-30') },
          { id: 'lab-002', text: 'Method validation completed', status: 'completed', deadline: new Date('2024-08-01') },
          { id: 'lab-003', text: 'Personnel competency assessed', status: 'in_progress', deadline: new Date('2024-10-01') },
          { id: 'lab-004', text: 'Equipment calibration program updated', status: 'overdue', deadline: new Date('2024-09-15') },
          { id: 'lab-005', text: 'Proficiency testing participation', status: 'pending', deadline: new Date('2024-11-01') }
        ],
        overallStatus: 'at_risk',
        completion: 60,
        priority: 'high'
      },
      {
        id: 'gdp-001',
        framework: 'GDP',
        category: 'Distribution',
        title: 'Good Distribution Practice',
        description: 'Guidelines for distribution of medicinal products',
        requirements: [
          { id: 'gdp-001', text: 'Distribution authorization current', status: 'completed', deadline: new Date('2024-05-15') },
          { id: 'gdp-002', text: 'Cold chain validation documented', status: 'completed', deadline: new Date('2024-07-30') },
          { id: 'gdp-003', text: 'Transportation qualification studies', status: 'in_progress', deadline: new Date('2024-10-20') }
        ],
        overallStatus: 'on_track',
        completion: 90,
        priority: 'low'
      }
    ];

    setChecklistData(mockChecklistData);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'in_progress': return 'text-primary';
      case 'pending': return 'text-muted-foreground';
      case 'overdue': return 'text-error';
      case 'at_risk': return 'text-warning';
      case 'on_track': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-success/10';
      case 'in_progress': return 'bg-primary/10';
      case 'pending': return 'bg-muted';
      case 'overdue': return 'bg-error/10';
      case 'at_risk': return 'bg-warning/10';
      case 'on_track': return 'bg-success/10';
      default: return 'bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in_progress': return 'Clock';
      case 'pending': return 'Circle';
      case 'overdue': return 'AlertTriangle';
      case 'at_risk': return 'AlertCircle';
      case 'on_track': return 'CheckCircle';
      default: return 'Circle';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded?.has(sectionId)) {
      newExpanded?.delete(sectionId);
    } else {
      newExpanded?.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const filteredData = checklistData?.filter(item => {
    if (selectedFramework !== 'all' && item?.framework !== selectedFramework) return false;
    if (filterStatus !== 'all' && item?.overallStatus !== filterStatus) return false;
    return true;
  });

  const formatDeadline = (date) => {
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `${diffDays} days remaining`;
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Compliance Checklist</h2>
          <p className="text-sm text-muted-foreground">Track regulatory requirements across frameworks</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={selectedFramework}
            onChange={(e) => setSelectedFramework(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
          >
            <option value="all">All Frameworks</option>
            <option value="GMP">GMP</option>
            <option value="EMA">EMA Guidelines</option>
            <option value="ISO 17025">ISO 17025</option>
            <option value="GDP">GDP</option>
          </select>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="in_progress">In Progress</option>
            <option value="at_risk">At Risk</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>
      </div>
      {/* Checklist Items */}
      <div className="space-y-4">
        {filteredData?.map((item) => (
          <div key={item?.id} className="bg-card border border-border rounded-lg overflow-hidden">
            {/* Section Header */}
            <div 
              className="p-4 cursor-pointer hover:bg-muted/30 transition-colors duration-150"
              onClick={() => toggleSection(item?.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Icon 
                    name={expandedSections?.has(item?.id) ? 'ChevronDown' : 'ChevronRight'} 
                    size={20} 
                    className="text-muted-foreground"
                  />
                  <div>
                    <div className="flex items-center space-x-3 mb-1">
                      <h3 className="text-lg font-medium text-card-foreground">{item?.title}</h3>
                      <span className="px-2 py-1 text-xs font-medium bg-secondary/10 text-secondary rounded-full">
                        {item?.framework}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(item?.priority)} bg-current/10`}>
                        {item?.priority?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{item?.description}</p>
                    <p className="text-xs text-muted-foreground mt-1">Category: {item?.category}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className="flex items-center space-x-2 mb-1">
                      <Icon 
                        name={getStatusIcon(item?.overallStatus)} 
                        size={16} 
                        className={getStatusColor(item?.overallStatus)}
                      />
                      <span className={`text-sm font-medium ${getStatusColor(item?.overallStatus)}`}>
                        {item?.overallStatus?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {item?.completion}% Complete
                    </div>
                  </div>
                  
                  <div className="w-16">
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          item?.completion === 100 ? 'bg-success' : 
                          item?.completion >= 75 ? 'bg-primary' : 
                          item?.completion >= 50 ? 'bg-warning' : 'bg-error'
                        }`}
                        style={{ width: `${item?.completion}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Expanded Requirements */}
            {expandedSections?.has(item?.id) && (
              <div className="border-t border-border bg-muted/20">
                <div className="p-4 space-y-3">
                  {item?.requirements?.map((req) => (
                    <div key={req?.id} className="flex items-center justify-between p-3 bg-card rounded-lg border border-border">
                      <div className="flex items-center space-x-3">
                        <Icon 
                          name={getStatusIcon(req?.status)} 
                          size={16} 
                          className={getStatusColor(req?.status)}
                        />
                        <div>
                          <p className="text-sm font-medium text-card-foreground">{req?.text}</p>
                          <p className="text-xs text-muted-foreground">
                            Deadline: {req?.deadline?.toLocaleDateString('en-GB')} - {formatDeadline(req?.deadline)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBgColor(req?.status)} ${getStatusColor(req?.status)}`}>
                          {req?.status?.replace('_', ' ')?.toUpperCase()}
                        </span>
                        {req?.status !== 'completed' && (
                          <Button variant="outline" size="xs">
                            Update
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="px-4 pb-4">
                  <Button variant="outline" size="sm" className="w-full">
                    <Icon name="Plus" size={16} className="mr-2" />
                    Add Requirement
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {filteredData?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="CheckCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No compliance items match your current filters</p>
        </div>
      )}
    </div>
  );
};

export default ComplianceChecklist;